import React, { useState } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { isMarineService, formatMarineLine } from "../../util/marineService";

export const ServiceDiscountModal = ({ 
  show, 
  onHide, 
  service,
  onApplyDiscount 
}) => {
  // Initialize with existing discount values if any
  const [discountMode, setDiscountMode] = useState(service.service_discount > 0 ? "amount" : "percent");
  const [discountAmount, setDiscountAmount] = useState(service.service_discount || 0);
  const [discountPercent, setDiscountPercent] = useState(
    service.service_discount ? 
    ((service.service_discount / service.service_cost) * 100).toFixed(2) : 
    0
  );
  
  // Calculate the other value when one changes
  const handleAmountChange = (amount) => {
    const validAmount = Math.min(Math.max(0, amount), service.service_cost);
    setDiscountAmount(validAmount);
    
    // Avoid division by zero
    if (service.service_cost > 0) {
      setDiscountPercent(((validAmount / service.service_cost) * 100).toFixed(2));
    } else {
      setDiscountPercent(0);
    }
  };
  
  const handlePercentChange = (percent) => {
    const validPercent = Math.min(Math.max(0, percent), 100);
    setDiscountPercent(validPercent);
    setDiscountAmount((service.service_cost * validPercent / 100).toFixed(2));
  };
  
  const handleDiscountModeChange = (mode) => {
    setDiscountMode(mode);
  };

  const handleClearDiscount = () => {
    setDiscountAmount(0);
    setDiscountPercent(0);
  };
  
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Service Discount: {service.description}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Service Cost: ${service.service_cost.toFixed(2)}</Form.Label>
            {isMarineService(service) && (
              <Form.Text className="text-muted d-block">
                {formatMarineLine(service)}
              </Form.Text>
            )}
          </Form.Group>
          
          <Form.Group>
            <Form.Label>Discount Type</Form.Label>
            <div className="mb-3">
              <Form.Check
                inline
                type="radio"
                id="discount-amount"
                label="Amount ($)"
                checked={discountMode === "amount"}
                onChange={() => handleDiscountModeChange("amount")}
              />
              <Form.Check
                inline
                type="radio"
                id="discount-percent"
                label="Percentage (%)"
                checked={discountMode === "percent"}
                onChange={() => handleDiscountModeChange("percent")}
              />
            </div>
          </Form.Group>
          
          {discountMode === "amount" ? (
            <Form.Group>
              <Form.Label>Discount Amount ($)</Form.Label>
              <InputGroup>
                <InputGroup.Prepend>
                  <InputGroup.Text>$</InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  type="number"
                  min="0"
                  max={service.service_cost}
                  step="0.01"
                  value={discountAmount}
                  onChange={(e) => handleAmountChange(parseFloat(e.target.value) || 0)}
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Equivalent to {discountPercent}% off
              </Form.Text>
            </Form.Group>
          ) : (
            <Form.Group>
              <Form.Label>Discount Percentage (%)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={discountPercent}
                  onChange={(e) => handlePercentChange(parseFloat(e.target.value) || 0)}
                />
                <InputGroup.Append>
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
              <Form.Text className="text-muted">
                Equivalent to ${discountAmount} off
              </Form.Text>
            </Form.Group>
          )}
          
          <div className="bg-light p-3 mt-3 rounded">
            <div className="d-flex justify-content-between">
              <span>Original Price:</span>
              <span className="font-weight-bold">${service.service_cost.toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between text-success">
              <span>Discount:</span>
              <span className="font-weight-bold">-${parseFloat(discountAmount).toFixed(2)}</span>
            </div>
            <hr className="my-2" />
            <div className="d-flex justify-content-between">
              <span>Final Price:</span>
              <span className="font-weight-bold">${(service.service_cost - parseFloat(discountAmount)).toFixed(2)}</span>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {parseFloat(discountAmount) > 0 && (
          <Button 
            variant="outline-danger" 
            onClick={handleClearDiscount}
          >
            Clear Discount
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button 
          variant="primary" 
          onClick={() => {
            onApplyDiscount(service._uid || service.service_id, parseFloat(discountAmount), parseFloat(discountPercent));
            onHide();
          }}
        >
          Apply Discount
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ServiceDiscountModal;